/*
  Bloom Boom delivery clock — the single source of truth for "can we deliver today?"
  copy across the storefront (2026-09-02).

  Contract
  --------
  * Any element with data-bb-promise="<variant>" gets its text replaced with the
    copy for the current Los Angeles time. Server-rendered text stays as the
    fallback until this runs, so keep that text true at any hour.
  * data-bb-promise-wrap="<variant>" sets the text of the first <a>/<button> inside.
  * <html data-bb-delivery-state="same_day|next_day|next_open_day"> is kept in sync
    so sections can show/hide pieces with CSS.
  * window.BBDeliveryClock.state() / .copy() / .onChange(fn) for custom renderers
    (the urgency bar and sticky CTA use these for their countdown digits).
  * Debug: append ?bb_clock=Tue-20:00, ?bb_clock=Sun-12:00 or
    ?bb_clock=2026-09-07-10:00 to any page to simulate a moment.

  Business rules (owner, 2026-09-02): open Monday–Saturday, closed Sunday and the
  dates listed below; same-day when ordered before 4:00 PM Pacific; deliveries run
  from 11 AM on the next open day otherwise.
*/
(function () {
  if (window.BBDeliveryClock) return;

  var CONFIG = {
    timeZone: 'America/Los_Angeles',
    cutoffHour: 16,
    cutoffLabel: '4 PM',
    openDays: [1, 2, 3, 4, 5, 6],
    closedDates: ['2026-09-07'],
    deliveryStartLabel: '11 AM'
  };
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var listeners = [];
  var simulated = null;
  var lastKey = null;

  var formatter = null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: CONFIG.timeZone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    });
  } catch (error) {
    return; // Old browser: keep the server-rendered fallback text.
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  function now() {
    if (simulated) return simulated;
    var bag = {};
    formatter.formatToParts(new Date()).forEach(function (part) { bag[part.type] = part.value; });
    var hour = parseInt(bag.hour, 10);
    if (hour === 24) hour = 0;
    return {
      dow: SHORT.indexOf(bag.weekday),
      hour: hour,
      minute: parseInt(bag.minute, 10),
      second: parseInt(bag.second, 10),
      iso: bag.year + '-' + bag.month + '-' + bag.day
    };
  }

  function addDays(iso, days) {
    var d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  function isOpen(dow, iso) {
    if (CONFIG.openDays.indexOf(dow) === -1) return false;
    return !iso || CONFIG.closedDates.indexOf(iso) === -1;
  }

  function state() {
    var t = now();
    var minutesNow = t.hour * 60 + t.minute;
    var cutoffMinutes = CONFIG.cutoffHour * 60;
    if (isOpen(t.dow, t.iso) && minutesNow < cutoffMinutes) {
      var remainingSeconds = cutoffMinutes * 60 - (minutesNow * 60 + (t.second || 0));
      return { key: 'same_day', dayLabel: 'today', dayName: DAYS[t.dow], remainingSeconds: remainingSeconds, now: t };
    }
    for (var i = 1; i <= 8; i++) {
      var dow = (t.dow + i) % 7;
      if (isOpen(dow, t.iso ? addDays(t.iso, i) : null)) {
        return {
          key: i === 1 ? 'next_day' : 'next_open_day',
          dayLabel: i === 1 ? 'tomorrow' : DAYS[dow],
          dayName: DAYS[dow],
          closedToday: t.dow === 0 || !isOpen(t.dow, t.iso),
          now: t
        };
      }
    }
    return { key: 'next_open_day', dayLabel: 'the next open day', dayName: '', closedToday: true, now: t };
  }

  function spread(seconds) {
    var minutes = Math.ceil(seconds / 60);
    var h = Math.floor(minutes / 60), m = minutes % 60;
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
  }

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  function copy(s) {
    s = s || state();
    var cut = CONFIG.cutoffLabel, start = CONFIG.deliveryStartLabel;
    if (s.key === 'same_day') {
      return {
        'short': 'Order by ' + cut + ' for same-day delivery',
        'long': 'Order by ' + cut + ' today for same-day delivery.',
        'countdown': 'Order in ' + spread(s.remainingSeconds) + ' for same-day delivery',
        'badge': 'Same-Day Delivery',
        'subtext': 'Order by ' + cut + ' Mon–Sat',
        'dispatch': 'order by ' + cut + ' and it goes out today.',
        'cutoff-row': 'Same-day if you order by ' + cut,
        'cutoff-title': 'Order by ' + cut + ' today',
        'cutoff-desc': 'for same-day delivery. After ' + cut + ', we deliver the next open day.',
        'cta': 'Start a same-day order',
        'hero-desc': 'Hand-crafted luxury arrangements, delivered today',
        'announcement': 'Order by ' + cut + ' today for same-day delivery',
        'zip-hit': 'same-day if you order by ' + cut,
        'zip-hit-long': 'Same-day delivery is available when you order by ' + cut + ' today.',
        'urgency-after': ''
      };
    }
    var next = s.dayLabel, nextCap = cap(next);
    var reason = s.now && s.now.dow === 0 ? 'Closed Sundays' : (s.closedToday ? 'Closed today' : 'Same-day cutoff passed');
    return {
      'short': 'Order now for delivery ' + next,
      'long': reason + ' — order now and we deliver ' + next + ' from ' + start + '.',
      'countdown': reason + ' — order now for ' + (next === 'tomorrow' ? 'delivery tomorrow' : nextCap + ' delivery'),
      'badge': 'Next-Day Delivery',
      'subtext': 'Order now · delivered ' + next + ' from ' + start,
      'dispatch': 'order now and it goes out ' + next + '.',
      'cutoff-row': 'Next delivery: ' + nextCap + ' from ' + start,
      'cutoff-title': reason,
      'cutoff-desc': 'Order now and we deliver ' + next + ' from ' + start + '.',
      'cta': 'Order for ' + next,
      'hero-desc': 'Hand-crafted luxury arrangements, delivered ' + next,
      'announcement': 'Order now · delivered ' + next + ' from ' + start,
      'zip-hit': 'delivery ' + next,
      'zip-hit-long': reason + ' — order now and we deliver ' + next + ' from ' + start + '.',
      'urgency-after': reason + ' — next delivery: ' + nextCap + ' from ' + start
    };
  }

  function render() {
    var s = state();
    var c = copy(s);
    document.documentElement.setAttribute('data-bb-delivery-state', s.key);
    var nodes = document.querySelectorAll('[data-bb-promise]');
    for (var i = 0; i < nodes.length; i++) {
      var variant = nodes[i].getAttribute('data-bb-promise');
      if (c[variant] != null && nodes[i].textContent !== c[variant]) nodes[i].textContent = c[variant];
    }
    var wraps = document.querySelectorAll('[data-bb-promise-wrap]');
    for (var j = 0; j < wraps.length; j++) {
      var target = wraps[j].querySelector('a, button');
      var wrapVariant = wraps[j].getAttribute('data-bb-promise-wrap');
      if (target && c[wrapVariant] != null && target.textContent.trim() !== c[wrapVariant]) target.textContent = c[wrapVariant];
    }
    for (var k = 0; k < listeners.length; k++) {
      try { listeners[k](s, c); } catch (error) { /* one bad listener must not break the others */ }
    }
    if (s.key !== lastKey) {
      lastKey = s.key;
      try { window.dispatchEvent(new CustomEvent('bb:delivery-state', { detail: { state: s, copy: c } })); } catch (error) { /* ignore */ }
    }
  }

  function simulate(spec) {
    // 'Tue-20:00' | 'Sun-12:00' | '2026-09-07-10:00' | null to clear
    if (!spec) { simulated = null; render(); return; }
    var m = /^(\d{4}-\d{2}-\d{2})-(\d{1,2}):(\d{2})$/.exec(spec);
    if (m) {
      var d = new Date(m[1] + 'T12:00:00Z');
      simulated = { dow: d.getUTCDay(), hour: parseInt(m[2], 10), minute: parseInt(m[3], 10), second: 0, iso: m[1] };
    } else {
      var w = /^([A-Za-z]{3})-(\d{1,2}):(\d{2})$/.exec(spec);
      if (!w) return;
      var dow = SHORT.indexOf(cap(w[1].toLowerCase()));
      if (dow === -1) return;
      simulated = { dow: dow, hour: parseInt(w[2], 10), minute: parseInt(w[3], 10), second: 0, iso: null };
    }
    render();
  }

  window.BBDeliveryClock = {
    config: CONFIG,
    state: state,
    copy: function () { return copy(state()); },
    refresh: render,
    onChange: function (fn) { listeners.push(fn); try { fn(state(), copy(state())); } catch (error) { /* ignore */ } },
    simulate: simulate,
    formatClock: function (seconds) { var h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60; return pad(h) + ':' + pad(m) + ':' + pad(s); }
  };

  var debug = null;
  try { debug = new URLSearchParams(window.location.search).get('bb_clock'); } catch (error) { /* ignore */ }
  if (debug) simulate(debug); else render();
  window.setInterval(render, 1000);
  document.addEventListener('shopify:section:load', render);
})();
